<template>
  <div>
    <breadcrumbs
      :breadcrumb-items="breadcrumbItems"
    />
    <UserToolbar
      v-if="account && Object.keys(account).length !== 0"
      :account="account"
    />
    <b-card>
      <CreativeToolbar
        :banner="banner"
        closing
      />
    </b-card>

    <CreativeNavigation v-if="authUserData.role.includes('admin')" />

    <b-card class="card-statistics">
      <div class="card-statistics-tools">
        <div>
          <h4>{{ $t('Statistics') }}</h4>
        </div>

      </div>
      <div>
        <div class="d-flex align-items-center">
          <CampaignDateRangeFilter
            :value="rangePicker"
            :is-loading="loading"
            @refresh="getMergedStatsData"
            @input="toggleRange"
          />
        </div>
        <div
          v-if="rtbStatsData.length === 0"
          class="card-nodata"
        >
          <p class="card-text">
            {{ $t('sites.nodata_period') }}
          </p>
        </div>
      </div>
      <div>
        <div
          v-if="loading"
          class="text-center my-3"
        >
          <b-spinner label="Loading..." />
        </div>
        <b-table
          v-else-if="rtbStatsData.length"
          :items="rtbStatsData"
          :fields="rtbFields"
          striped
          responsive
          small
          :sort-by.sync="rtbSortBy"
          :sort-desc.sync="rtbSortDesc"
          :sort-direction="rtbSortDirection"
          show-empty
          foot-clone
          class="text-center mt-2"
        >
          <template #head(date)>
            {{ $t('stats.date') }}
          </template>
          <template #head(impressions)>
            {{ $t('stats.impressions') }}
          </template>
          <template #head(clicks)>
            {{ $t('stats.clicks') }}
          </template>
          <template #head(ctr)>
            CTR
          </template>
          <template #head(spent)>
            {{ $t('stats.spent') }}
          </template>

          <template #cell(date)="data">
            {{ data.value ? getFormattedDate(new Date(data.value)) : '' }}
          </template>
          <template #cell(impressions)="data">
            {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
          </template>
          <template #cell(clicks)="data">
            {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
          </template>
          <template #cell(ctr)="data">
            {{ data.item.clicks ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(data.item.clicks / data.item.impressions * 100) + '%' : 0 }}
          </template>
          <template #cell(spent)="data">
            {{ data.value ? currency(campaign.currency || 'USD', data.value) : 0 }}
          </template>

          <template #foot(date)="data">
            <strong :data-label="data.label">{{ $t('Records') }}: {{ rtbStatsData.length }}</strong>
          </template>
          <template #foot(impressions)>
            {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(rtbSum('impressions')) }}
          </template>
          <template #foot(clicks)>
            {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(rtbSum('clicks')) }}
          </template>
          <template #foot(ctr)>
            {{ rtbSum('clicks') ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(rtbSum('clicks') / rtbSum('impressions') * 100) : 0 }}%
          </template>
          <template #foot(spent)>
            {{ currency(campaign.currency || 'USD', rtbSum('spent')) }}
          </template>
        </b-table>
        <div
          v-else
          class="text-center text-muted my-3"
        >
          {{ $t('No data for this period') }}
        </div>
      </div>
    </b-card>

    <div
      v-if="graphicShow"
      id="blocks_stats"
      ref="blocks_stats"
    >
      <b-row v-if="statsBlocksLoading">
        <b-col>
          <b-card
            class="card-browser-states text-center"
          >
            <b-spinner label="Loading..." />
          </b-card>
        </b-col>
      </b-row>
      <b-row
        v-else-if="Object.keys(filterStatsBlocks).length !== 0 && !statsBlocksLoading"
        class="stats-blocks match-height"
      >
        <b-col
          v-if="filterStatsBlocks.imprByCountryCity.length > 1"
          md="4"
        >
          <countries-stats
            :subtitle="blockSubtitle"
            :country-city="filterStatsBlocks.imprByCountryCity"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByBrowserName.length > 1"
          md="4"
        >
          <browser-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByBrowserName"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByLanguage.length > 1"
          md="4"
        >
          <language-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByLanguage"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByDeviceType.length > 1"
          md="4"
        >
          <devices-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByDeviceType"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByOsName.length > 1"
          md="4"
        >
          <os-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByOsName"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByPlatform.length > 1"
          md="4"
        >
          <platform-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByPlatform"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByUniqueUsers > 0"
          md="3"
        >
          <unique-users-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByUniqueUsers"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprSiteByRequestUrl.length > 1"
          md="9"
        >
          <requests-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprSiteByRequestUrl"
          />
        </b-col>
      </b-row>
    </div>
  </div>
</template>
<script>
import { getUserData } from '@/auth/utils'
import Ripple from 'vue-ripple-directive'
import getFormattedDate, { getFormattedDateMonth } from '@/utils/date-formatter'
import currencyFormatter from '@/utils/currency-formatter'
import useJwt from '@/auth/jwt/useJwt'
import {
  BCard,
  BRow,
  BCol,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import UserToolbar from '@/views/dashboard/users/UserToolbar.vue'
import CreativeToolbar from '@/views/dashboard/creatives/CreativeToolbar.vue'
import CountriesStats from '@/views/ui/stats-blocks/CountriesStats.vue'
import BrowserStats from '@/views/ui/stats-blocks/BrowserStats.vue'
import LanguageStats from '@/views/ui/stats-blocks/LanguageStats.vue'
import DevicesStats from '@/views/ui/stats-blocks/DevicesStats.vue'
import OsStats from '@/views/ui/stats-blocks/OsStats.vue'
import RequestsStats from '@/views/ui/stats-blocks/RequestsStats.vue'
import PlatformStats from '@/views/ui/stats-blocks/PlatformStats.vue'
import UniqueUsersStats from '@/views/ui/stats-blocks/UniqueUsersStats.vue'
import CreativeNavigation from './CreativeNavigation.vue'
import CampaignDateRangeFilter from '../campaigns/CampaignDateRangeFilter.vue'

export default {
  components: {
    Breadcrumbs,
    UserToolbar,
    BCard,
    BRow,
    BCol,
    BSpinner,
    BTable,
    CreativeToolbar,
    CountriesStats,
    BrowserStats,
    LanguageStats,
    DevicesStats,
    OsStats,
    RequestsStats,
    PlatformStats,
    UniqueUsersStats,
    CampaignDateRangeFilter,
    CreativeNavigation,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: false,
      statsBlocksLoading: false,
      breadcrumbItems: [],
      account: {},
      campaign: {},
      banner: {},
      blockSubtitle: '',
      authUserData: getUserData(),
      currency: currencyFormatter,
      getFormattedDate,
      getFormattedDateMonth,
      rtbStatsData: [],
      statsBlocks: {
        show: {},
        click: {},
      },
      campaignesstats: {},
      campaignclickesstats: {},
      selectedTypeStats: 'month',
      selectedBlocksTypeStats: 'month',
      currentField: 'show',
      typeText: {
        month: this.$i18n.t('30 days'),
        week: this.$i18n.t('7 days'),
        day: this.$i18n.t('1 day'),
      },
      numDays: {
        month: 30,
        week: 7,
        day: 1,
      },
      graphicShow: false,
      rangePicker: [getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), getFormattedDate(new Date())],
      rtbFields: [
        { key: 'date', label: 'Дата', sortable: true },
        { key: 'impressions', label: 'Impressions', sortable: true },
        { key: 'clicks', label: 'Clicks', sortable: true },
        { key: 'ctr', label: 'CTR', sortable: true },
        { key: 'spent', label: 'Spent', sortable: true },
      ],
      rtbSortBy: 'date',
      rtbSortDesc: true,
      rtbSortDirection: 'desc',
    }
  },
  computed: {
    filterStatsBlocks() {
      return this.statsBlocks[this.currentField] || {}
    },
    selectedRangeFormated() {
      let dateFrom
      let dateTo
      const selectedRange = this.rangePicker
      if (Array.isArray(selectedRange)) {
        [dateFrom, dateTo] = selectedRange
      } else {
        [dateFrom, , dateTo] = selectedRange.split(' ')
      }

      return {
        from: getFormattedDate(new Date(dateFrom)),
        to: dateTo === undefined ? getFormattedDate(new Date(dateFrom)) : getFormattedDate(new Date(dateTo)),
      }
    },
  },
  watch: {
    currentField() {
      // this.statsBlocksLoading = true
      setTimeout(() => {
        this.statsBlocksLoading = false
      }, 500)
    },
  },
  created() {
    this.getUser()
    this.getCampaignData()
    this.getMergedStatsData()
    // this.getCampaignEstats()
    this.getCampaignClickesStats()
    this.blockSubtitle = `${this.selectedRangeFormated.from.replaceAll('-', '.')}-${this.selectedRangeFormated.to.replaceAll('-', '.')}`
  },
  methods: {
    toggleRange(selectedDates) {
      this.rangePicker = selectedDates
      this.blockSubtitle = `${this.selectedRangeFormated.from.replaceAll('-', '.')}-${this.selectedRangeFormated.to.replaceAll('-', '.')}`
      this.getMergedStatsData()
      if (this.graphicShow) {
        this.getCampaignEstats()
      }
      this.getCampaignClickesStats()
    },
    toggleGraph() {
      this.graphicShow = !this.graphicShow
      if (this.graphicShow) {
        this.getCampaignEstats()
        const firstTab = document.querySelector('.nav-tabs > li > a')
        if (firstTab && this.currentField === 'amount') {
          firstTab.click()
        }
        this.scrollToTop('#advanced_stats')
      }
    },
    async getMergedStatsData() {
      this.loading = true
      try {
        // Загружаем Phoenix данные (как на странице Phoenix)
        const phoenixParams = {
          campaign_id: this.$route.params.id,
          ad_id: this.$route.params.bannerId,
          type: 'daily',
          from: this.selectedRangeFormated.from,
          to: this.selectedRangeFormated.to,
          limit: 80,
        }
        const phoenixResponse = await useJwt.getCreativeStatsByTypeV2(phoenixParams)

        // Загружаем RTB данные (как на странице RtbStatsOverview)
        const rtbParams = {
          campaign_id: this.$route.params.id,
          ad_id: this.$route.params.bannerId,
          from: this.selectedRangeFormated.from,
          to: this.selectedRangeFormated.to,
          type: 'daily',
          limit: 80,
        }
        const rtbResponse = await useJwt.getPhoenixDspCampaign(rtbParams)

        // Объединяем данные по дате
        const phoenixData = phoenixResponse.data.items || []
        const rtbData = rtbResponse.data.items || []

        // Создаем Map для быстрого поиска данных по дате
        const phoenixMap = new Map()
        phoenixData.forEach(phoenixItem => {
          phoenixMap.set(phoenixItem.date, phoenixItem)
        })

        const rtbMap = new Map()
        rtbData.forEach(rtbItem => {
          rtbMap.set(rtbItem.date, rtbItem)
        })

        // Собираем все уникальные даты
        const allDates = new Set([
          ...phoenixData.map(item => item.date),
          ...rtbData.map(item => item.date),
        ])

        // Объединяем данные по всем датам
        this.rtbStatsData = Array.from(allDates).sort().map(date => {
          const phoenixItem = phoenixMap.get(date) || {}
          const rtbItem = rtbMap.get(date) || {}

          // Суммируем revenue из Phoenix (объект с разными ключами)
          const phoenixRevenue = typeof phoenixItem.revenue === 'object'
            ? Object.values(phoenixItem.revenue || {}).reduce((sum, val) => sum + (val || 0), 0)
            : (phoenixItem.revenue || 0)

          // Суммируем clicks из Phoenix (объект с разными ключами)
          const phoenixClicks = typeof phoenixItem.clicks === 'object'
            ? Object.values(phoenixItem.clicks || {}).reduce((sum, val) => sum + (val || 0), 0)
            : (phoenixItem.clicks || 0)

          return {
            date,
            impressions: (phoenixItem.impressions || 0) + (rtbItem.impressions || 0), // Sum Phoenix + RTB impressions
            clicks: phoenixClicks + (rtbItem.clicks || 0), // Sum Phoenix + RTB clicks
            spent: phoenixRevenue + (rtbItem.spent || 0), // Revenue из Phoenix + Spent из RTB
          }
        })
      } catch (error) {
        this.rtbStatsData = []
      } finally {
        this.loading = false
      }
    },
    async getCampaignEstats() {
      this.statsBlocksLoading = true
      this.statsBlocks.show = {}
      const currentDate = new Date()
      const params = {
        from: getFormattedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - this.numDays[this.selectedBlocksTypeStats])),
        to: getFormattedDate(currentDate),
      }
      const responseData = await useJwt.getCampaignEsStatsByType(this.$route.params.id, params)
      this.statsBlocks.show = responseData.data.campaignesstats
      this.statsBlocksLoading = false
    },
    async getCampaignClickesStats() {
      this.statsBlocksLoading = true
      this.statsBlocks.click = {}
      const currentDate = new Date()
      const params = {
        from: getFormattedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - this.numDays[this.selectedBlocksTypeStats])),
        to: getFormattedDate(currentDate),
      }
      const responseData = await useJwt.getCampaignClickesStats(this.$route.params.id, { from: params.from, to: params.to })
      this.statsBlocks.click = responseData.data.campaignclickesstats
      this.statsBlocksLoading = false
    },
    setField(field) {
      this.currentField = field
    },
    async getCampaignData() {
      const responseData = await useJwt.getCampaign(this.$route.params.id)
      this.campaign = responseData.data.campaign || {}
      await this.getBannerData()
      this.setBreabcrumbs()
    },
    async getBannerData() {
      const responseData = await useJwt.getBanner(this.$route.params.bannerId)
      this.banner = responseData.data.banner || {}
    },
    async getUser() {
      const responseData = await useJwt.getUser(this.$route.params.userId)
      this.account = responseData.data.account || {}
      this.getCampaignData()
    },
    rtbSum(key) {
      return this.rtbStatsData.reduce((acc, r) => acc + (r[key] || 0), 0)
    },
    setBreabcrumbs() {
      this.breadcrumbItems = [
        {
          text: 'users',
          to: '/users',
        },
        {
          text: this.account.name,
          to: `/users/${this.$route.params.userId}`,
        },
        {
          text: 'Campaigns',
          to: `/users/${this.$route.params.userId}/campaigns`,
        },
        {
          text: this.campaign.title,
          to: `/users/${this.$route.params.userId}/campaigns/${this.$route.params.id}`,
        },
        {
          text: 'Creatives',
          to: `/users/${this.$route.params.userId}/campaigns/${this.$route.params.id}/creatives`,
        },
        {
          text: this.banner.title || 'Creative',
          active: true,
        },
      ]
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
<style scoped lang="scss">
.card-statistics .card-body {
  min-height: 190px;
}

.card-statistics-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

@media (max-width: 440px) {
  [dir] #app {
    .card-statistics-tools {
      flex-wrap: wrap;
    }
  }
}
</style>
